import React, { useState, useEffect } from 'react';
import api from '../../api';
import AdminLayout from '../../components/AdminLayout';

const STATUS_COLORS = {
  pending:  { bg: '#fef3c7', color: '#92400e' },
  approved: { bg: '#d1fae5', color: '#065f46' },
  rejected: { bg: '#fee2e2', color: '#991b1b' },
};

export default function AdminReturns() {
  const [returns, setReturns]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('');
  const [selected, setSelected]     = useState(null);
  const [actionForm, setActionForm] = useState({ status: '', admin_notes: '', issue_refund: false });
  const [saving, setSaving]         = useState(false);

  const load = (status = filter) => {
    const q = status ? `?status=${status}` : '';
    api.get(`/returns${q}`).then(r => { setReturns(r.data.returns); setLoading(false); });
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const openDetail = (ret) => {
    setSelected(ret);
    setActionForm({ status: '', admin_notes: ret.admin_notes || '', issue_refund: false });
  };

  const handleAction = async (e) => {
    e.preventDefault();
    if (!actionForm.status) return;
    setSaving(true);
    try {
      await api.put(`/returns/${selected.id}/status`, actionForm);
      // Issue Stripe refund if checkbox ticked and approved
      if (actionForm.status === 'approved' && actionForm.issue_refund) {
        try {
          await api.post('/payments/refund', { order_id: selected.order_id });
        } catch (refundErr) {
          alert(`Return approved, but refund failed: ${refundErr.response?.data?.error || 'Unknown error'}. You can retry the refund from the Orders page.`);
        }
      }
      setSelected(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update return');
    } finally {
      setSaving(false);
    }
  };

  const counts = {
    all:      returns.length,
    pending:  returns.filter(r => r.status === 'pending').length,
    approved: returns.filter(r => r.status === 'approved').length,
    rejected: returns.filter(r => r.status === 'rejected').length,
  };

  return (
    <AdminLayout title="Returns">
      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[['', 'All'], ['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected']].map(([val, label]) => (
          <button
            key={val}
            className={`btn btn-sm ${filter === val ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setFilter(val)}
          >
            {label} {val === '' ? `(${counts.all})` : val === 'pending' ? `(${counts.pending})` : val === 'approved' ? `(${counts.approved})` : `(${counts.rejected})`}
          </button>
        ))}
      </div>

      {loading ? <div className="spinner" /> : returns.length === 0 ? (
        <div className="empty-state">
          <div className="icon">↩️</div>
          <h2>No return requests</h2>
          <p>{filter ? `No ${filter} returns.` : 'No returns have been submitted yet.'}</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Order</th>
                <th>Customer</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {returns.map(ret => (
                <tr key={ret.id}>
                  <td>{ret.id}</td>
                  <td>#{ret.order_id} <span style={{ fontSize: 12, color: '#6b7280' }}>${(ret.order_total || 0).toFixed(2)}</span></td>
                  <td>
                    {ret.customer_name || 'Guest'}
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>{ret.customer_email}</div>
                  </td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ret.reason}</td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      ...STATUS_COLORS[ret.status],
                    }}>
                      {ret.status.charAt(0).toUpperCase() + ret.status.slice(1)}
                    </span>
                  </td>
                  <td style={{ fontSize: 13 }}>{new Date(ret.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="btn btn-sm btn-outline" onClick={() => openDetail(ret)}>Review</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail / Action Modal */}
      {selected && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
        }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0 }}>Return #{selected.id}</h2>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              {[
                ['Order', `#${selected.order_id}`],
                ['Order Total', `$${(selected.order_total || 0).toFixed(2)}`],
                ['Customer', selected.customer_name],
                ['Email', selected.customer_email],
                ['Submitted', new Date(selected.created_at).toLocaleString()],
                ['Status', selected.status.charAt(0).toUpperCase() + selected.status.slice(1)],
              ].map(([k, v]) => (
                <div key={k}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>{k}</div>
                  <div style={{ fontSize: 14, marginTop: 2 }}>{v}</div>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 16, padding: '14px 16px', background: '#f9fafb', borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>REASON</div>
              <div style={{ fontWeight: 600 }}>{selected.reason}</div>
              {selected.details && <div style={{ fontSize: 14, color: '#374151', marginTop: 8 }}>{selected.details}</div>}
            </div>

            {selected.admin_notes && selected.status !== 'pending' && (
              <div style={{ marginBottom: 16, padding: '12px 14px', background: '#eff6ff', borderRadius: 8, fontSize: 14 }}>
                <strong>Admin notes:</strong> {selected.admin_notes}
              </div>
            )}

            {selected.status === 'pending' ? (
              <form onSubmit={handleAction}>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Decision *</label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {['approved', 'rejected'].map(s => (
                      <button
                        key={s} type="button"
                        onClick={() => setActionForm(f => ({ ...f, status: s }))}
                        style={{
                          flex: 1, padding: '10px', borderRadius: 8, fontWeight: 600, fontSize: 14,
                          border: `2px solid ${s === 'approved' ? '#10b981' : '#ef4444'}`,
                          background: actionForm.status === s ? (s === 'approved' ? '#10b981' : '#ef4444') : 'white',
                          color: actionForm.status === s ? 'white' : (s === 'approved' ? '#065f46' : '#991b1b'),
                          cursor: 'pointer',
                        }}
                      >
                        {s === 'approved' ? '✓ Approve' : '✗ Decline'}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Notes to customer (optional)</label>
                  <textarea
                    value={actionForm.admin_notes}
                    onChange={e => setActionForm(f => ({ ...f, admin_notes: e.target.value }))}
                    placeholder="e.g. Please ship items back to our warehouse at..."
                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, minHeight: 80, resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
                {actionForm.status === 'approved' && (
                  <div style={{ marginBottom: 20, padding: '12px 14px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox" id="issue_refund"
                      checked={actionForm.issue_refund}
                      onChange={e => setActionForm(f => ({ ...f, issue_refund: e.target.checked }))}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <label htmlFor="issue_refund" style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#065f46' }}>
                      💳 Also issue full Stripe refund automatically
                    </label>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setSelected(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={!actionForm.status || saving}>
                    {saving ? 'Saving…' : 'Submit Decision'}
                  </button>
                </div>
              </form>
            ) : (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <span style={{ padding: '8px 20px', borderRadius: 20, fontWeight: 700, ...STATUS_COLORS[selected.status] }}>
                  {selected.status === 'approved' ? '✓ Return Approved' : '✗ Return Declined'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
